import initHelpHero, { HelpHero } from 'helphero';
import { action, observable } from 'mobx';

import * as DB from '../adl-gen/totalinteraction/futurefit/db';
import { AppClientConfig } from '../adl-gen/totalinteraction/futurefit/uiconfig';
import { Auth } from '../auth';
import { I18n, LangCode } from '../internationalization/i18n-hoc';
import { isLoading, MaybeLoading } from '../loader/loader';
import {
  jsonToManagerSettings,
  jsonToSettings,
  pathFromRoute,
  Route,
  routeFromPath,
  validJson
} from '../routing/app-routes';
import { Router } from '../routing/router';
import { assertNever } from '../util/types';

import { AppLinks } from './app-links';
import { LoggedIn, LoggedInSuper, MaybeLoggedIn } from './identity-store';
import { SelectedResults } from './manager-quiz-results-store';
import { StoreFactory } from './store-factory';
import { AppState, AdminStateClient, WalkthroughType, walkthroughs } from './app-types';

/**
 * Top level app data and actions, including navigation.
 */
export class AppStore implements AppLinks {
  // keep identity state regardless of app state
  // but this is set ONCE and never updated...
  @observable.deep identity: MaybeLoading<MaybeLoggedIn> = isLoading();

  // app state (includes typed identity state)
  @observable.deep state: AppState;

  /**
   * HelpHero walkthrough initialization. Skillseeker ID = EqLntqt2CtS, same for all clients
   */
  /// TODO: Add to secrets/keys
  walkthroughManager: HelpHero = initHelpHero('EqLntqt2CtS');

  constructor(
    /** The authentication handler */
    public readonly auth: Auth,

    /** Router which is the bridge to the browser location */
    public readonly router: Router,

    /** Interface for constructing new and changed stores on request */
    public readonly storeFactory: StoreFactory,

    /** Handler for database translations - used to pass to walkthroughs via user properties */
    public readonly i18n: I18n
  ) {
    this.state = { kind: 'identity-loading', identity: isLoading() };
  }

  async init(): Promise<void> {
    this.router.registerRouteChangeListener(this.onRouteChange);
    this.onRouteChange('appStore:init');
    return;
  }

  /** Updates state based on route changes */
  @action onRouteChange = (context?: string) => {
    // No longer checks for auth0-callback - that is done separately in an async call

    let route = routeFromPath(this.router.getCurrentPath());
    if (route === undefined) {
      // tslint:disable-next-line: no-console
      console.debug(context, `onRouteChange: invalid route ${this.router.getCurrentPath()}, authenticated=${this.auth.isAuthenticated()}`);
      // Map invalid routes to home or login
      route = this.auth.isAuthenticated() ? { route: 'home' } : { route: 'login' };
    }

    if (route.route === 'logout') {
      // tslint:disable-next-line: no-console
      console.debug(context, 'onRouteChange: logout');
      this.auth.logout();
      this.state = {
        kind: 'identity-not-logged-in',
        identity: { kind: 'not-logged-in' }
      };
      return;
    }

    if (route.route === 'login') {
      // tslint:disable-next-line: no-console
      console.debug(context, 'onRouteChange: login');
      this.auth.login(`${context}:onRouteChange`);
      this.state = {
        kind: 'identity-loading',
        identity: isLoading()
      };
      return;
    }

    // 2x switch block to handle MaybeLoading<MaybeLoggedIn> = this.identity;
    // tslint:disable-next-line: no-console
    console.debug(context, 'onRouteChange: identity = ', this.identity.kind);
    switch (this.identity.kind) {
      case 'loading':
        this.state = {
          kind: 'identity-loading',
          identity: isLoading()
        };
        return;
      case 'error':
        this.state = {
          kind: 'identity-error',
          identity: this.identity
        };
        return;
      case 'value':
        // tslint:disable-next-line: no-console
        console.debug(context, 'onRouteChange: identity value = ', this.identity.value.kind);
        switch (this.identity.value.kind) {
          case 'not-logged-in':
            this.state = {
              kind: 'identity-not-logged-in',
              identity: this.identity.value
            };
            return;
          case 'unregistered':
            this.state = {
              kind: 'identity-unregistered',
              identity: this.identity.value
            };
            return;
          default:
          // fall through for LoggedIn identitiy
        }
        // fall through for LoggedIn identitiy
        break;

      default:
        return assertNever(this.identity, 'unknown state');
    }

    /**
     *  Subsequent routes can use this.identity.value : LoggedIn or LoggedInSuper
     */
    const loggedInIdentity: LoggedIn | LoggedInSuper = this.identity.value;

    // tslint:disable-next-line: no-console
    console.info(`OnRouteChange got ${loggedInIdentity.kind} logged in identity`);

    /**
     * Use identity to load HelpHero for walkthrough
     */
    if (loggedInIdentity.kind === 'logged-in' && loggedInIdentity.appClient.walkthroughsEnabled) {
      this.configWalkthroughs(loggedInIdentity);
    }

    /// Now for clarity and not an endless series of if/then statements...
    if (loggedInIdentity.kind === 'super-logged-in') {
      // TODO: ApplicationRole.super implicit
      this.setSuperState(route, loggedInIdentity);
      return;
    } else {
      if (loggedInIdentity.role === DB.ApplicationRole.admin) {
        this.setAdminState(route, loggedInIdentity);
        return;
      } else if (loggedInIdentity.role === DB.ApplicationRole.user) {
        this.setUserState(route, loggedInIdentity);
        return;
      } else {
        // TODO: DB.ApplicationRole.super is redundant here
        return;
      }
    }
  };

  setSuperState = (route: Route, identity: LoggedInSuper) => {
    switch (route.route) {
      case 'home':
        {
          this.state = {
            kind: 'admin',
            identity,
            state: {
              page: 'clients',
              store: this.storeFactory.makeAppClientStore(identity)
            }
          };
        }
        return;

      case 'admin': {
        switch (route.page) {
          case 'clients':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  store: this.storeFactory.makeAppClientStore(identity)
                }
              };
            }
            return;
          case 'client':
            {
              const state: AdminStateClient = {
                page: route.page,
                view: route.view,
                appClientId: route.appClientId,
                id: route.id,
                store: this.storeFactory.makeAppClientStore(identity, route.id)
              };

              this.state = {
                kind: 'admin',
                identity,
                state
              };
            }
            return;
          case 'users':
            {
              if (route.appClientId === undefined) {
                this.navigateTo({ route: 'home' });
              } else {
                this.state = {
                  kind: 'admin',
                  identity,
                  state: {
                    page: route.page,
                    // view: route.view,
                    appClientId: route.appClientId,
                    // clientStore: this.storeFactory.makeAppClientStore(identity, route.appClientId),
                    store: this.storeFactory.makeAppUserStore(identity, route.appClientId)
                  }
                };
              }
            }
            return;
          case 'groups':
            {
              if (route.appClientId === undefined) {
                this.navigateTo({ route: 'home' });
              } else {
                this.state = {
                  kind: 'admin',
                  identity,
                  state: {
                    page: route.page,
                    // view: route.view,
                    appClientId: route.appClientId,
                    store: this.storeFactory.makeUserGroupStore(identity, route.appClientId)
                  }
                };
              }
            }
            return;
          case 'user':
            {
              if (route.appClientId === undefined) {
                this.navigateTo({ route: 'home' });
              } else {
                if (route.view === 'upload') {
                  this.state = {
                    kind: 'admin',
                    identity,
                    state: {
                      page: route.page,
                      view: route.view,
                      appClientId: route.appClientId,
                      id: route.id,
                      store: this.storeFactory.makeAppUserUploadStore(identity, route.appClientId)
                    }
                  };
                } else if (route.view === 'upload-results') {
                  this.state = {
                    kind: 'admin',
                    identity,
                    state: {
                      page: route.page,
                      view: route.view,
                      appClientId: route.appClientId,
                      id: route.id,
                      store: this.storeFactory.makeAppUserUploadStore(identity, route.appClientId, route.id)
                    }
                  };
                } else {
                  this.state = {
                    kind: 'admin',
                    identity,
                    state: {
                      page: route.page,
                      view: route.view,
                      appClientId: route.appClientId,
                      id: route.id,
                      store: this.storeFactory.makeAppUserStore(identity, route.appClientId, route.id)
                    }
                  };
                }
              }
            }
            return;
          case 'group':
            {
              if (route.appClientId === undefined) {
                this.navigateTo({ route: 'home' });
              } else {
                this.state = {
                  kind: 'admin',
                  identity,
                  state: {
                    page: route.page,
                    view: route.view,
                    appClientId: route.appClientId,
                    id: route.id,
                    // TODO: May not work - unless we define both - which complicates routes
                    //clientStore: this.storeFactory.makeAppClientStore(identity, route.appClientId),
                    store: this.storeFactory.makeUserGroupStore(identity, route.appClientId, route.id)
                  }
                };
              }
            }
            return;
          case 'competencies-skills':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  // view: route.view,
                  store: this.storeFactory.makeCompetencyStore(identity)
                }
              };
            }
            return;
          case 'competency':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  view: route.view,
                  id: route.id,
                  store: this.storeFactory.makeCompetencyStore(identity, route.id)
                }
              };
            }
            return;
          case 'skill':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  view: route.view,
                  id: route.id,
                  store: this.storeFactory.makeSkillStore(identity, route.id)
                }
              };
            }
            return;
          case 'roles':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  // view: route.view,
                  store: this.storeFactory.makeWorkRoleStore(identity)
                }
              };
            }
            return;
          case 'role':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  view: route.view,
                  id: route.id,
                  store: this.storeFactory.makeWorkRoleStore(identity, undefined, route.id)
                }
              };
            }
            return;
          case 'role-builder':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  view: route.view,
                  id: route.id,
                  store: this.storeFactory.makeWorkRoleStore(identity, undefined, route.id)
                }
              };
            }
            return;
          case 'quiz-admin':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  view: route.view,
                  workRoleId: route.workRoleId,
                  store: this.storeFactory.makeQuizAdminStore(identity, undefined, route.workRoleId)
                }
              };
            }
            return;
          case 'reports':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  view: undefined,
                  appClientId: route.appClientId,
                  id: undefined,
                  store: this.storeFactory.makeReportStore(identity, route.appClientId)
                }
                // }
              };
            }
            return;
          case 'questions':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  appClientId: undefined,
                  store: this.storeFactory.makeQuestionStore(identity)
                }
              };
            }
            return;
          case 'question':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  view: route.view,
                  appClientId: undefined,
                  skillId: route.skillId,
                  id: route.id,
                  store: this.storeFactory.makeQuestionStore(identity, route.skillId, route.id)
                }
              };
            }
            return;
          case 'recommendations':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  appClientId: undefined,
                  store: this.storeFactory.makeTrainingOfferingStore(identity)
                }
              };
            }
            return;
          case 'training-offering':
            {
              this.state = {
                kind: 'admin',
                identity,
                state: {
                  page: route.page,
                  view: route.view,
                  appClientId: undefined,
                  // skillId: route.skillId,
                  id: route.id,
                  store: this.storeFactory.makeTrainingOfferingStore(identity, route.id)
                }
              };
            }
            return;
        }
      }
    }
  };

  setAdminState = (route: Route, identity: LoggedIn) => {
    console.debug('DEBUG setAdminState: route:', route);
    switch (route.route) {
      case 'home':
        {
          this.state = {
            kind: 'admin',
            identity,
            state: {
              page: 'client',
              appClientId: identity.appUserWithDbId.value.appClientId,
              id: identity.appUserWithDbId.value.appClientId,
              store: this.storeFactory.makeAppClientStore(identity, identity.appUserWithDbId.value.appClientId)
              // userStore: this.storeFactory.makeAppUserStore(identity, identity.appUserWithDbId.value.appClientId),
            }
          };
        }
        return;

      case 'admin': {
        if (
          route.page === 'clients' ||
          route.page === 'competencies-skills' ||
          route.page === 'competency' ||
          route.page === 'skill'
        ) {
          this.navigateTo({ route: 'home' });
        } else {
          switch (route.page) {
            case 'client':
              {
                if (route.view === 'new') {
                  this.navigateTo({ route: 'home' });
                } else {
                  this.state = {
                    kind: 'admin',
                    identity,
                    state: {
                      page: route.page,
                      view: route.view,
                      appClientId: route.appClientId,
                      id: route.id,
                      store: this.storeFactory.makeAppClientStore(identity, route.id)
                    }
                  };
                }
              }
              return;

            case 'users':
              {
                if (route.appClientId === undefined) {
                  this.navigateTo({ route: 'home' });
                  return;
                } else {
                  this.state = {
                    kind: 'admin',
                    identity,
                    state: {
                      page: route.page,
                      appClientId: route.appClientId,
                      store: this.storeFactory.makeAppUserStore(identity, route.appClientId)
                    }
                  };
                }
              }
              return;

            case 'groups':
              {
                if (route.appClientId === undefined) {
                  this.navigateTo({ route: 'home' });
                  return;
                } else {
                  this.state = {
                    kind: 'admin',
                    identity,
                    state: {
                      page: route.page,
                      // view: route.view,
                      appClientId: route.appClientId,
                      // clientStore: this.storeFactory.makeAppClientStore(identity, route.appClientId),
                      store: this.storeFactory.makeUserGroupStore(identity, route.appClientId)
                    }
                  };
                }
              }
              return;

            case 'user':
              {
                if (route.appClientId === undefined) {
                  this.navigateTo({ route: 'home' });
                } else {
                  if (route.view === 'upload' || route.view === 'upload-results') {
                    this.state = {
                      kind: 'admin',
                      identity,
                      state: {
                        page: route.page,
                        view: route.view,
                        appClientId: route.appClientId,
                        id: route.id,
                        store: this.storeFactory.makeAppUserUploadStore(identity, route.appClientId, route.id)
                      }
                    };
                  } else {
                    this.state = {
                      kind: 'admin',
                      identity,
                      state: {
                        page: route.page,
                        view: route.view,
                        appClientId: route.appClientId,
                        id: route.id,
                        store: this.storeFactory.makeAppUserStore(identity, route.appClientId, route.id)
                      }
                    };
                  }
                }
              }
              return;

            case 'group':
              {
                if (route.appClientId === undefined) {
                  this.navigateTo({ route: 'home' });
                } else {
                  this.state = {
                    kind: 'admin',
                    identity,
                    state: {
                      page: route.page,
                      view: route.view,
                      appClientId: route.appClientId,
                      id: route.id,
                      store: this.storeFactory.makeUserGroupStore(identity, route.appClientId, route.id)
                    }
                  };
                }
              }
              return;

            case 'roles':
              {
                this.state = {
                  kind: 'admin',
                  identity,
                  state: {
                    page: route.page,
                    // view: route.view,
                    appClientId: route.appClientId,
                    store: this.storeFactory.makeWorkRoleStore(identity, route.appClientId)
                  }
                };
              }
              return;
            case 'role':
              {
                this.state = {
                  kind: 'admin',
                  identity,
                  state: {
                    page: route.page,
                    view: route.view,
                    appClientId: route.appClientId,
                    id: route.id,
                    store: this.storeFactory.makeWorkRoleStore(identity, route.appClientId, route.id)
                  }
                };
              }
              return;
            case 'role-builder':
              {
                this.state = {
                  kind: 'admin',
                  identity,
                  state: {
                    page: route.page,
                    view: route.view,
                    appClientId: route.appClientId,
                    id: route.id,
                    store: this.storeFactory.makeWorkRoleStore(identity, route.appClientId, route.id)
                  }
                };
              }
              return;
            case 'quiz-admin':
              {
                this.state = {
                  kind: 'admin',
                  identity,
                  state: {
                    page: route.page,
                    view: route.view,
                    appClientId: route.appClientId,
                    workRoleId: route.workRoleId,
                    store: this.storeFactory.makeQuizAdminStore(identity, route.appClientId, route.workRoleId)
                  }
                };
              }
              return;
            case 'reports':
              {
                if (route.appClientId === undefined) {
                  this.navigateTo({ route: 'home' });
                } else {
                  this.state = {
                    kind: 'admin',
                    identity,
                    state: {
                      page: route.page,
                      view: undefined,
                      appClientId: route.appClientId,
                      id: undefined,
                      store: this.storeFactory.makeReportStore(identity, route.appClientId)
                    }
                  };
                }
              }
              return;
          }
          // }
        }
        return;
      }

      default:
        this.setUserState(route, identity);
        return;
    }
  };

  setUserState = (route: Route, identity: LoggedIn) => {
    switch (route.route) {
      case 'home':
        {
          if (identity.isManager) {
            this.state = {
              kind: 'manager',
              identity,
              managerPortalStore: this.storeFactory.makeManagerPortalStore(identity)
            };
          } else {
            this.state = {
              kind: 'my-assessments',
              identity,
              userQuizDataStore: this.storeFactory.makeUserQuizDataStore(identity),
              walkThroughFn: this.setWalkthroughFn('my-assessments', identity.appClient)
            };
          }
        }
        return;

      case 'my-assessments':
        {
          this.state = {
            kind: 'my-assessments',
            identity,
            userQuizDataStore: this.storeFactory.makeUserQuizDataStore(identity),
            walkThroughFn: this.setWalkthroughFn('my-assessments', identity.appClient)
          };
        }
        return;

      case 'quiz':
        {
          this.state = {
            kind: 'quiz',
            quizStore: this.storeFactory.makeQuizStore(route.userQuizId, identity),
            identity
          };
        }
        return;
      case 'self-assessment':
        {
          this.state = {
            kind: 'self-assessment',
            quizStore: this.storeFactory.makeQuizStore(route.userQuizId, identity),
            identity
          };
        }
        return;

      case 'quiz-results':
        {
          let settings;
          if (route.settingsJson !== undefined) {
            if (!validJson(route.settingsJson)) {
              this.navigateTo({
                ...route,
                settingsJson: undefined
              });
              return;
            }
            const json = JSON.parse(route.settingsJson);
            settings = jsonToSettings(json);
          }

          /// FF2-427 disable navigation if not available
          if (
            settings !== undefined &&
            /// Explicit check cos "undefined" implies false
            ((settings.guidanceAvailable !== undefined && !settings.guidanceAvailable && route.page === 'guidance') ||
              (settings.recommendationsAvailable !== undefined &&
                !settings.recommendationsAvailable &&
                route.page === 'recommendations'))
          ) {
            return;
          }

          this.state = {
            kind: 'quiz-results',
            page: route.page,
            quizResultsStore: this.storeFactory.makeQuizResultsStore(
              route.quizId,
              route.userQuizId,
              identity,
              settings
            ),
            plotKeyToggleState: this.storeFactory.makePlotKeyToggleStore(),
            identity,
            walkThroughFn: this.setWalkthroughFn('quiz-results', identity.appClient),
            walkThroughMgr: this.walkthroughManager
          };
        }
        return;

      case 'manager':
        {
          if (!identity.isManager) {
            this.navigateTo({ route: 'home' });
          } else {
            this.state = {
              kind: 'manager',
              identity,
              managerPortalStore: this.storeFactory.makeManagerPortalStore(identity)
            };
          }
        }
        return;

      case 'manager-quiz-results':
        {
          if (!identity.isManager) {
            this.navigateTo({ route: 'home' });
          } else {
            let settings;
            if (route.settingsJson !== undefined) {
              if (!validJson(route.settingsJson)) {
                this.navigateTo({
                  ...route,
                  settingsJson: undefined
                });
                return;
              }
              const json = JSON.parse(route.settingsJson);
              settings = jsonToManagerSettings(json);
            }

            /// FF2-427 disable navigation if not available
            if (
              settings !== undefined &&
              /// Explicit check cos "undefined" implies false
              ((settings.guidanceAvailable !== undefined && !settings.guidanceAvailable && route.page === 'guidance') ||
                (settings.recommendationsAvailable !== undefined &&
                  !settings.recommendationsAvailable &&
                  route.page === 'recommendations'))
            ) {
              return;
            }

            this.state = {
              kind: 'manager-quiz-results',
              page: route.page,
              identity,
              plotKeyToggleState: this.storeFactory.makePlotKeyToggleStore(),
              managerQuizResultsStore: this.storeFactory.makeManagerQuizResultsStore(identity, route.quizId, settings),
              walkThroughFn: this.setWalkthroughFn('manager-quiz-results', identity.appClient),
              walkThroughMgr: this.walkthroughManager
            };
          }
        }
        return;

      case 'admin':
        {
          this.navigateTo({ route: 'home' });
        }
        return;
    }
  };

  navigateTo = (route: Route) => {
    // tslint:disable-next-line: no-console
    console.info('navigateTo: route', route.route);
    this.router.go(pathFromRoute(route));
  };

  navigateWithProps = (route: Route, props: SelectedResults) => {
    this.router.go(pathFromRoute(route), props);
  };

  resolveWalkthrough = (kind: WalkthroughType, page: string, lang: LangCode): string | undefined => {
    const wts = walkthroughs.find(wt => wt.kind === kind);
    if (wts !== undefined) {
      const pageWts = wts.walkthroughs.find(pagewt => pagewt.page === page);
      if (pageWts !== undefined) {
        const wtIdForLang = pageWts.id[lang] || pageWts.id[LangCode.en];
        // tslint:disable-next-line: no-console
        console.info(
          `OnRouteChange HelpHero Walkthrough start tour for '${kind}' id '${wtIdForLang}' title '${pageWts.title}'`
        );
        return wtIdForLang;
      }
    }
    // Throwing an error here is too restrictive if we delete the walkthrough
    // tslint:disable-next-line: no-console
    console.error(`Walkthrough not found for ${kind} page ${page} lang ${lang}`);
    return undefined;
    // throw new Error(`Walkthrough not found for ${kind} page ${page} lang ${lang}`);
  };

  runRootWalkthrough = (identity: LoggedIn) => {
    if (identity.appClient.walkthroughsEnabled) {
      this.walkthroughManager.update({ lang: this.i18n.langCode });
      const wtName = identity.isManager ? 'manager-root' : 'participant-root';
      const wtIdToUse = this.resolveWalkthrough(wtName, '', this.i18n.langCode);
      /**
       * Start Page 1 tour. Matches URL "/", so won't start if landing on any other URL
       */
      if (wtIdToUse) {
        this.walkthroughManager.startTour(wtIdToUse, { skipIfAlreadySeen: identity.appClient.skipIfAlreadySeen });
        const skipOrNotSkip = identity.appClient.skipIfAlreadySeen ? 'skip' : 'not skip';
        // tslint:disable-next-line: no-console
        console.info(`Walkthrough matching '/', will ${skipOrNotSkip} if already seen`);
      } else {
        // tslint:disable-next-line: no-console
        console.error("Walkthrough '", wtName, "' not found");
      }
    } else {
      /// -- [ The following comment may no longer be true since 'auto-start' switched off on tours in HelpHero ] --
      ///   > If tours are disabled, we need to actually stop them from running
      /// This does prevent a tour from running on the client, and also stops the user being marked by cookie as
      /// having already gone through the tour, so that if walkthroughs are later enabled, they will run at least
      /// once (or more than once if skipIfAlreadySeen is set to false).

      this.walkthroughManager.cancelTour();
    }
  };

  setWalkthroughFn = (kind: WalkthroughType, appClient: AppClientConfig) => (page: string): void => {
    if (appClient.walkthroughsEnabled) {
      this.walkthroughManager.update({ lang: this.i18n.langCode });
      /**
       * Start tour, if matched.
       * Matches URL "/manager-quiz-results/{+any}/[page]/{*any}", so won't start if landing on any other URL
       * my-assessments - no page
       * quiz-results - page but two /{+any}s
       */
      const wtIdToUse = this.resolveWalkthrough(kind, page, this.i18n.langCode);
      if (wtIdToUse) {
        this.walkthroughManager.startTour(wtIdToUse, { skipIfAlreadySeen: appClient.skipIfAlreadySeen });
        let matchUrl;
        switch (kind) {
          case 'manager-quiz-results':
            matchUrl = `/${kind}/{+any}/${page}/{*any}`;
            break;
          case 'my-assessments':
            matchUrl = `/${kind}/{*any}`;
            break;
          case 'quiz-results':
            matchUrl = `/${kind}/{+any}/{+any}/${page}/{*any}`;
        }
        const skipOrNotSkip = appClient.skipIfAlreadySeen ? 'skip' : 'not skip';
        // tslint:disable-next-line: no-console
        console.info(`Walkthrough matching ${matchUrl}, will ${skipOrNotSkip} if already seen`);
      }
    } else {
      /// -- [ The following comment may no longer be true since 'auto-start' switched off on tours in HelpHero ] --
      ///   > If tours are disabled, we need to actually stop them from running
      /// This does prevent a tour from running on the client, and also stops the user being marked by cookie as
      /// having already gone through the tour, so that if walkthroughs are later enabled, they will run at least
      /// once (or more than once if skipIfAlreadySeen is set to false).
      this.walkthroughManager.cancelTour();
    }
  };

  configWalkthroughs = (identity: LoggedIn) => {
    const walkThroughVersion = 0.88;
    this.walkthroughManager.identify(identity.appUserWithDbId.id, {
      name: identity.appUserWithDbId.value.fullname, // Used
      lang: this.i18n.langCode, // Used
      fallbackLang: this.i18n.fallbackLang.code,
      isManager: identity.isManager, // Used
      role: identity.role === DB.ApplicationRole.super ? 'admin' : 'user',
      email: identity.appUserWithDbId.value.email,
      client: identity.appClient.name,
      // TODO: rename this field - but will need to rewrite walkthroughs
      recommendationsEnabled: identity.appClient.recommendationsVisible,
      displayHeadlineRatio: identity.appClient.displayHeadlineRatio,
      displayHeadlineText: identity.appClient.displayHeadlineText,
      noTrafficLights: false, // Used/updated
      noGuidance: false, // /updated
      noRecommendations: false, // Used/updated
      noSkillBars: false, // Used/updated
      noCompetencyBarsSlideout: false, // Used/updated
      recommendationMethod: 'relevance',
      rolesSelected: false,
      version: walkThroughVersion,
      created_at: 1643088516 // Unix timestamp
    });
    // tslint:disable-next-line: no-console
    console.info(`OnRouteChange HelpHero Walkthrough version ${walkThroughVersion}; identity set up`);
    this.runRootWalkthrough(identity);
  };
}



// WEBPACK FOOTER //
// ./src/stores/app-store.ts